import { useEffect, useState } from "react";
import { api, apiError, CITIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CalendarDays, Users, DollarSign } from "lucide-react";

export function hostingFee(slots) {
  const n = Number(slots) || 0;
  if (n <= 3) return 100;
  if (n <= 5) return 140;
  return 200;
}

function minStartDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

export default function OrganizerDashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [applicants, setApplicants] = useState([]);

  const load = () => api.get("/my/events").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openApplicants = async (ev) => {
    setSelected(ev);
    try { const { data } = await api.get(`/events/${ev.id}/applications`); setApplicants(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const decide = async (appId, status) => {
    try {
      await api.put(`/applications/${appId}`, { status });
      setApplicants((a) => a.map((x) => x.id === appId ? { ...x, status } : x));
      toast.success(`Vendor ${status}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const payFee = async (ev) => {
    try {
      const { data } = await api.post(`/events/${ev.id}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Organizer Dashboard</h1>
          <p className="text-slate-500">Hi {user.name} — post events & manage vendor applications.</p>
        </div>
        <EventDialog onCreated={load} onPay={payFee} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="space-y-3">
          <h2 className="font-display text-xl font-bold">Your events</h2>
          {events.length === 0 && <p className="text-slate-400">No events yet. Create your first one.</p>}
          {events.map((e) => {
            const pending = e.payment_status !== "paid";
            return (
              <div key={e.id} className={`bg-white rounded-2xl border p-5 transition-colors ${selected?.id === e.id ? "border-[#0FB5A6]" : "border-teal-100"}`} data-testid={`org-event-${e.id}`}>
                <button onClick={() => openApplicants(e)} className="w-full text-left">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold">{e.title}</p>
                    {pending
                      ? <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">Pending payment</span>
                      : <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-bold">Published</span>}
                  </div>
                  <div className="flex gap-4 text-sm text-slate-500 mt-1 flex-wrap">
                    <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" /> {e.start_date}</span>
                    <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {e.spots_filled}/{e.spots_total} slots</span>
                    <span>{e.city}</span>
                  </div>
                </button>
                {pending && (
                  <Button onClick={() => payFee(e)} className="mt-3 rounded-full font-bold h-9 bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid={`pay-event-${e.id}`}>
                    <DollarSign className="w-4 h-4 mr-1" /> Pay ${Number(e.hosting_fee || hostingFee(e.spots_total)).toFixed(0)} to publish
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div>
          <h2 className="font-display text-xl font-bold">{selected ? `Applicants · ${selected.title}` : "Select an event"}</h2>
          <div className="mt-3 space-y-3">
            {selected && applicants.length === 0 && <p className="text-slate-400">No applications yet.</p>}
            {applicants.map((a) => (
              <div key={a.id} className="bg-white rounded-2xl border border-teal-100 p-5" data-testid={`applicant-${a.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-bold">{a.truck_name}</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${a.status === "approved" ? "bg-green-100 text-green-700" : a.status === "rejected" ? "bg-red-100 text-red-600" : "bg-teal-100 text-teal-700"}`}>{a.status}</span>
                </div>
                {a.message && <p className="text-sm text-slate-500 mt-1">{a.message}</p>}
                {a.status === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <Button onClick={() => decide(a.id, "approved")} className="rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid={`approve-${a.id}`}>Approve</Button>
                    <Button onClick={() => decide(a.id, "rejected")} variant="outline" className="rounded-full font-bold border-teal-200" data-testid={`reject-${a.id}`}>Decline</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function EventDialog({ onCreated, onPay }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ title: "", description: "", city: "Green Bay", venue: "", address: "", start_date: "", end_date: "", spots_total: 3, image_url: "", lat: 44.5133, lng: -88.0133, vendor_fee: 0 });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const fee = hostingFee(f.spots_total);

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/events", { ...f, vendor_fee: Number(f.vendor_fee) || 0, spots_total: Number(f.spots_total) });
      toast.success("Event created — redirecting to payment…");
      setOpen(false);
      onCreated();
      onPay(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform" data-testid="new-event-btn"><Plus className="w-4 h-4 mr-1" /> New event</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Create an event</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <F label="Title"><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="event-title" /></F>
          <F label="Description"><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="event-description" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="City">
              <Select value={f.city} onValueChange={(v) => set("city", v)}>
                <SelectTrigger data-testid="event-city-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Venue"><Input value={f.venue} onChange={(e) => set("venue", e.target.value)} data-testid="event-venue" /></F>
            <F label="Address"><Input value={f.address} onChange={(e) => set("address", e.target.value)} /></F>
            <F label="Image URL"><Input value={f.image_url} onChange={(e) => set("image_url", e.target.value)} /></F>
            <F label="Start date (min 7 days out)"><Input type="date" min={minStartDate()} value={f.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="event-start" /></F>
            <F label="End date"><Input type="date" value={f.end_date} onChange={(e) => set("end_date", e.target.value)} /></F>
            <F label="Food truck slots"><Input type="number" min="2" max="10" value={f.spots_total} onChange={(e) => set("spots_total", e.target.value)} data-testid="event-slots" /></F>
          </div>

          <div className="rounded-xl border border-teal-100 bg-teal-50/50 p-4 flex items-center justify-between" data-testid="event-fee-preview">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Hosting fee ({f.spots_total <= 3 ? "small" : f.spots_total <= 5 ? "medium" : "large"} event)</p>
              <p className="text-xs text-slate-400">Paid upfront to publish. Must be at least 7 days before the event.</p>
            </div>
            <span className="font-display text-3xl font-black text-[#0FB5A6]" data-testid="event-fee-amount">${fee}</span>
          </div>

          <Button onClick={create} disabled={saving} className="w-full rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid="create-event-btn">{saving ? "Creating…" : `Create & pay $${fee}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label>{children}</div>;
